import { confirmMinersOnOff } from "@components/global/ConfirmMinersOnOff";
import { GetBuilding_RequestTypes } from "@mobx/api/building";
import { EditBuilding_RequestTypes, EditLocationMeta_RequestTypes, GetLocation_RequestTypes } from "@mobx/api/locations";
import { MinerControl_RequestTypes } from "@mobx/api/miner";
import apiService, { RequestControl } from "@mobx/services/http";
import { MinerHashEvent } from "@services/http/socket/responses";
import store_ from "@store";
import { EQUIPMENT_TYPE } from "@store/Equipments/actionTypes";
import debouncer from "@utils/debouncer";
import { Point } from "framer-motion";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { Connection, Edge, EdgeChange, NodeChange, Viewport, XYPosition } from "reactflow";
import { fromRackItem3, IRack, IRackItem3, isMinerItem3, toRackItem3, TypePartialRackItem3 } from "../locations/types";
import { createDragStore, DragStore } from "./dragStore";
import { createEditorStore, EditorStore3 } from "./editorStore";
import { MinersChangesTable, RacksChangesTable } from "./ops";
import { deserializePlacedEditorData, serializePlacedEditorData } from "./serialize";
import {
  ConsumerKind3,
  ConsumersDropMeta3,
  CURRENT_SHEME_VERSION3,
  DEFAULT_KIND3,
  DraggedElementData3,
  isValidElectricalScheme3,
  NodeSignal3,
  NodeType3,
  PlaceEditorsData3,
  PlaceID3,
  SerializedPlaceEditorsData3,
  signalControl3,
} from "./types";

const debounceSave = debouncer(200);

const mergeSavedHints = (
  currentSaved: boolean,
  ...hints: Array<boolean | undefined>
) => hints.some((hint) => hint === false)
  ? false
  : hints.some((hint) => hint === true)
  ? true
  : currentSaved;

export interface PlaceStore {
  placeID: PlaceID3;
  isLocation: boolean;
  isInitializing: boolean;
  initialized: boolean;
  version: string;
  currentKind: EQUIPMENT_TYPE;
  saved: boolean;
  afterKindChange: boolean;
  kinds: { [kind in EQUIPMENT_TYPE]: EditorStore3 };
  drag: DragStore;
  setCurrentKind: (kind: EQUIPMENT_TYPE) => void;
  setLoading: (on: boolean) => void;
  connect: (edge: Edge<any> | Connection) => void;
  applyNodeChanges: (changes: NodeChange[]) => void;
  applyEdgeChanges: (changes: EdgeChange[]) => void;
  applyUndoRedo: (undo: boolean) => void;
  save: (data: PlaceEditorsData3) => void;
  load: () => Promise<void>;
  init: () => Promise<void>;
  clear: () => void;
  newNode: (type: NodeType3, pos: XYPosition) => void;
  removeNode: (nodeID: number | string) => void;
  removeEdge: (nodeID: number) => void;
  setNodeName: (nodeID: number, name: string) => void;
  editNodeInfo: (nodeID: string, name: string, notes: string, color: string, type: EQUIPMENT_TYPE) => void;
  mergeRacks: (racks: IRack[]) => void;
  removeRacks: (rackIDs: number[]) => void;
  changeRackItems: (rackID: number, items: TypePartialRackItem3[]) => void;
  reportMinersSignal: (signal: NodeSignal3, nodes: Set<string>, success: boolean) => void;
  confirmAndMinersControl(signal: NodeSignal3, miners: IRackItem3[], controlledNodes: Set<string>): Promise<boolean>;
  minersControl: (signal: NodeSignal3, miners: IRackItem3[], nodes: Set<string>, success: boolean) => Promise<void>;
  minersInRackControl: (signal: NodeSignal3, miners: IRackItem3[], nodes: Set<string>) => Promise<void>;
  changeMinerState: (ev: MinerHashEvent) => void;
  sendSignal: (signal: NodeSignal3, nodeID: string) => void;
  setUnsaved: () => void;
  setEditorLock: (lock: boolean) => void;
  saveEditorViewport: (v: Viewport) => void;
  toggleToDrag: (id: string, kind: ConsumerKind3, elementDragID: string, data: unknown) => void;
  drop: (eqID: string | undefined, els: DraggedElementData3[], point: Point) => ConsumersDropMeta3 | undefined;
  setDrag: (isDrag: boolean) => void;
  clearDrag: (keep?: boolean) => void;
  initDragMouse: (mousePos?: Point, shift?: Point) => void;
  moveDragMouse: (shift: Point, mousePos?: Point) => void;
  get currentEditor(): EditorStore3;
}

export const createPlaceStore = (params: {
  placeID: PlaceID3;
  isLocation: boolean;
  rc: RequestControl;
}): PlaceStore => {
  const { placeID, isLocation, rc } = params;

  const getBuilding = apiService.createRequest(GetBuilding_RequestTypes); // admin/object/get
  const editBuilding = apiService.createRequest(EditBuilding_RequestTypes); // admin/object/update
  const getLocation = apiService.createRequest(GetLocation_RequestTypes); // admin/location/get
  const editLocation = apiService.createRequest(EditLocationMeta_RequestTypes); // admin/location/add
  const controlMiner = apiService.createRequest(MinerControl_RequestTypes); // admin/miner/control

  const lastColorRef = { value: 0 };
  let initPromise: Promise<void> | undefined;
  const nextColorIndex = () => {
    const next = lastColorRef.value;
    lastColorRef.value += 1;
    return next;
  };

  const self: PlaceStore = {
    placeID,
    isLocation,
    isInitializing: false,
    initialized: false,
    version: CURRENT_SHEME_VERSION3,
    currentKind: DEFAULT_KIND3,
    saved: false,
    afterKindChange: false,
    kinds: {
      [EQUIPMENT_TYPE.ELECTRICAL]: createEditorStore({
        placeID,
        kind: EQUIPMENT_TYPE.ELECTRICAL,
        nextColorIndex,
      }),
      [EQUIPMENT_TYPE.NETWORK]: createEditorStore({
        placeID,
        kind: EQUIPMENT_TYPE.NETWORK,
        nextColorIndex,
      }),
    },
    drag: createDragStore(),

    setCurrentKind(kind: EQUIPMENT_TYPE) {
      self.afterKindChange = true;
      self.currentKind = kind;
    },

    setLoading(on: boolean) {
      self.currentEditor.setLoading(on);
    },

    connect(edge: Edge<any> | Connection) {
      applyToCurrent((ed) => ed.connect(edge));
    },

    applyNodeChanges(changes: NodeChange[]) {
      applyToCurrent((ed) => ed.applyNodeChanges(changes), true);
    },

    applyEdgeChanges(changes: EdgeChange[]) {
      applyToCurrent((ed) => ed.applyEdgeChanges(changes));
    },

    applyUndoRedo(undo: boolean) {
      applyToCurrent((ed) => ed.applyUndoRedo(undo));
    },

    save(data: PlaceEditorsData3) {
      saveDebounced(data);
    },

    async load() {
      const data = await getBuildingData();
      if (data === false) return;

      runInAction(() => {
        if (data) {
          self.version = data.version;
          self.saved = true;
          self.kinds[EQUIPMENT_TYPE.ELECTRICAL].replaceData(
            data.kinds[EQUIPMENT_TYPE.ELECTRICAL]
          );
          self.kinds[EQUIPMENT_TYPE.NETWORK].replaceData(
            data.kinds[EQUIPMENT_TYPE.NETWORK]
          );
        } else {
          resetToInitial();
        }
      });
    },

    async init() {
      if (self.initialized) return;
      if (initPromise) return initPromise;

      self.isInitializing = true;
      initPromise = (async () => {
        const data = await getBuildingData();
        if (data === false) return;

        runInAction(() => {
          if (data) {
            self.version = data.version;
            self.saved = true;
            self.initialized = true;
            self.afterKindChange = false;
            self.drag.clearDraggedElements();
            self.drag.clearDrag();
            self.kinds[EQUIPMENT_TYPE.ELECTRICAL].replaceData(
              data.kinds[EQUIPMENT_TYPE.ELECTRICAL]
            );
            self.kinds[EQUIPMENT_TYPE.NETWORK].replaceData(
              data.kinds[EQUIPMENT_TYPE.NETWORK]
            );
          } else {
            resetToInitial();
            self.initialized = true;
          }
        });
      })();

      try {
        await initPromise;
      } finally {
        runInAction(() => {
          initPromise = undefined;
          self.isInitializing = false;
        });
      }
    },

    clear() {
      applyToCurrent((ed) => ed.clear());
    },

    newNode(type: NodeType3, pos: XYPosition) {
      applyToCurrent((ed) => ed.newNode(type, pos));
    },

    removeNode(nodeID: number | string) {
      applyToCurrent((ed) => ed.removeNode(nodeID));
    },

    removeEdge(nodeID: number) {
      applyToCurrent((ed) => ed.removeEdge(nodeID));
    },

    setNodeName(nodeID: number, name: string) {
      applyToCurrent((ed) => ed.setNodeName(nodeID, name));
    },

    editNodeInfo(nodeID: string, name: string, notes: string, color: string, _type: EQUIPMENT_TYPE) {
      applyToCurrent((ed) => ed.editNodeInfo(nodeID, name, notes, color));
    },

    mergeRacks(racks: IRack[]) {
      const rtab: RacksChangesTable = {};
      for (const rack of racks) {
        rtab[rack.id] = rack;
      }
      const savedEl = self.kinds[EQUIPMENT_TYPE.ELECTRICAL].mergeRacks(rtab);
      const savedNt = self.kinds[EQUIPMENT_TYPE.NETWORK].mergeRacks(rtab);
      self.saved = mergeSavedHints(self.saved, savedEl, savedNt);
      self.afterKindChange = false;
    },

    removeRacks(rackIDs: number[]) {
      const rackIDSet = new Set(rackIDs);
      const savedEl = self.kinds[EQUIPMENT_TYPE.ELECTRICAL].removeRacks(rackIDSet);
      const savedNt = self.kinds[EQUIPMENT_TYPE.NETWORK].removeRacks(rackIDSet);
      self.saved = mergeSavedHints(self.saved, savedEl, savedNt);
      self.afterKindChange = false;
    },

    changeRackItems(rackID: number, items: TypePartialRackItem3[]) {
      const mtab: MinersChangesTable = {};
      for (const item of items) {
        if (isMinerItem3(item)) mtab[item.miner_id] = { ...toRackItem3(item), rackID };
      }
      applyToCurrent((ed) => ed.changeRackItems(rackID, items, mtab));
    },

    reportMinersSignal(signal: NodeSignal3, nodes: Set<string>, success: boolean) {
      applyToCurrent((ed) => ed.reportMinersControl(signalControl3[signal], nodes, success));
    },

    async minersControl(signal: NodeSignal3, miners: IRackItem3[], nodes: Set<string>, success: boolean) {
      if (success) {
        success = !!(await controlMiner(
          rc.createRequestHandle(),
          signalControl3[signal],
          miners.map((m) => m.miner_id)
        ));
      }
      self.reportMinersSignal(signal, nodes, success);
    },

    async confirmAndMinersControl(signal: NodeSignal3, miners: IRackItem3[], controlledNodes: Set<string>) {
      const reduxStore = store_();
      if (!reduxStore) return false;
      const ctrl = signalControl3[signal];
      const ok = await confirmMinersOnOff(reduxStore.dispatch, miners.map(fromRackItem3), ctrl);
      if (ok) {
        const r = await controlMiner(rc.createRequestHandle(), ctrl, miners.map(m => m.miner_id));
        self.reportMinersSignal(signal, controlledNodes, !!r);
        if (r) {
          return true;
        }
      } else {
        //dbg.log("FAIL 3");
        self.reportMinersSignal(signal, controlledNodes, false)
      }
      return false;
    },    

    async minersInRackControl(signal: NodeSignal3, miners: IRackItem3[], nodes: Set<string>) {
      await controlMiner(
        rc.createRequestHandle(),
        signalControl3[signal],
        miners.map((m) => m.miner_id)
      );
      self.reportMinersSignal(signal, nodes, true);
    },

    changeMinerState(ev: MinerHashEvent) {
      applyToCurrent((ed) => ed.changeMinerState(ev));
    },

    sendSignal(signal: NodeSignal3, nodeID: string) {
      applyToCurrent((ed) => ed.sendSignal(nodeID, signal));
    },

    setUnsaved() {
      self.saved = false;
    },

    setEditorLock(lock: boolean) {
      applyToCurrent((ed) => ed.setEditorLock(lock));
    },

    saveEditorViewport(v: Viewport) {
      applyToCurrent((ed) => ed.saveEditorViewport(v));
    },

    toggleToDrag(id: string, kind: ConsumerKind3, elementDragID: string, data: unknown) {
      self.drag.toggleToDrag(id, kind, elementDragID, data);
    },

    drop(_eqID: string | undefined, els: DraggedElementData3[], point: Point) {
      if (self.currentEditor.locked) return;
      let dropMeta: ConsumersDropMeta3 | undefined = undefined;
      if (els.length) {
        const result = applyToCurrent(
          (ed) => ed.createConsumers(els, point),
          false,
          (res) => res.savedHint
        );
        dropMeta = result.meta;
      } else {
        applyToCurrent(() => false);
      }
      self.drag.clearDraggedElements();
      self.drag.clearDrag();
      self.afterKindChange = false;
      return dropMeta;
    },

    setDrag(isDrag: boolean) {
      self.drag.setDrag(isDrag);
    },

    clearDrag(keep?: boolean) {
      self.drag.clearDraggedElements();
      self.drag.clearDrag(keep);
    },

    initDragMouse(mousePos?: Point, shift?: Point) {
      self.drag.initDragMouse(mousePos, shift);
    },

    moveDragMouse(shift: Point, mousePos?: Point) {
      self.drag.moveDragMouse(shift, mousePos);
    },

    get currentEditor() {
      return self.kinds[self.currentKind];
    },
  };

  const saveDebounced = debounceSave<[PlaceEditorsData3]>(async (data) => {
    if (!data || !placeID) return;
    const payload = { ElectricScheme: serializePlacedEditorData(data) };
    const res = self.isLocation
      ? await editLocation(rc.createRequestHandle(), placeID, payload)
      : await editBuilding(rc.createRequestHandle(), placeID, undefined, payload);
    if (!res) return;
    runInAction(() => {
      self.saved = true;
    });
  });

  const resetToInitial = () => {
    lastColorRef.value = 0;
    self.currentKind = DEFAULT_KIND3;
    self.initialized = false;
    self.saved = false;
    self.version = CURRENT_SHEME_VERSION3;
    self.afterKindChange = false;
    self.kinds[EQUIPMENT_TYPE.ELECTRICAL].resetToInitialState();
    self.kinds[EQUIPMENT_TYPE.NETWORK].resetToInitialState();
    self.drag.clearDraggedElements();
    self.drag.clearDrag();
  };

  const applyToCurrent = <T>(
    fn: (ed: EditorStore3) => T,
    guardAfterKindChange = false,
    getSavedHint?: (result: T) => boolean | undefined
  ): T => {
    const wasAfterKindChange = self.afterKindChange;
    const result = fn(self.currentEditor);
    const savedHint = getSavedHint
      ? getSavedHint(result)
      : typeof result === "boolean" || typeof result === "undefined"
      ? result
      : undefined;
    let nextSaved = savedHint;
    if (nextSaved === undefined || (guardAfterKindChange && wasAfterKindChange)) {
      nextSaved = self.saved;
    }
    self.saved = nextSaved;
    self.afterKindChange = false;
    return result;
  };

  const getBuildingData = async (): Promise<PlaceEditorsData3 | false | undefined> => {
    if (!placeID) return false;
    self.setLoading(true);
    try {
      const r = self.isLocation
        ? await getLocation(rc.createRequestHandle(), placeID)
        : await getBuilding(rc.createRequestHandle(), placeID);
      if (!r || !r.Record?.meta?.ElectricScheme) return false;
      let data: PlaceEditorsData3 | undefined = deserializePlacedEditorData(r.Record.meta.ElectricScheme  as SerializedPlaceEditorsData3);
      if (data.version < CURRENT_SHEME_VERSION3) data = undefined;
      if (data && !isValidElectricalScheme3(data.kinds[EQUIPMENT_TYPE.ELECTRICAL])) data = undefined;
      return data;
    } finally {
      self.setLoading(false);
    }
  };

  return makeObservable(self, {
    isLocation: observable,
    isInitializing: observable,
    initialized: observable,
    version: observable,
    currentKind: observable,
    saved: observable,
    afterKindChange: observable,
    kinds: observable,
    drag: observable,
    currentEditor: computed,
    setCurrentKind: action,
    setLoading: action,
    connect: action,
    applyNodeChanges: action,
    applyEdgeChanges: action,
    applyUndoRedo: action,
    save: action,
    load: action,
    init: action,
    clear: action,
    newNode: action,
    removeNode: action,
    removeEdge: action,
    setNodeName: action,
    editNodeInfo: action,
    mergeRacks: action,
    removeRacks: action,
    changeRackItems: action,
    reportMinersSignal: action,
    minersControl: action,
    minersInRackControl: action,
    changeMinerState: action,
    sendSignal: action,
    setUnsaved: action,
    setEditorLock: action,
    saveEditorViewport: action,
    toggleToDrag: action,
    drop: action,
    setDrag: action,
    clearDrag: action,
    initDragMouse: action,
    moveDragMouse: action,
  });
};
